import { Box, Drawer, Stack } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTranslatedSnackbar } from 'src/hooks/useTranslatedSnackbar';
import { useNavigate } from 'react-router-dom';
import { conversationApi } from 'src/api/conversation';
import { visitApi } from 'src/api/visit';
import { PATH_DASHBOARD } from 'src/routes/paths';
import type { SendChatMessageRequest } from 'woofwoof-api';
import { GetVisitByIdStatusEnum } from 'woofwoof-api';
// auth
import { useUserContext } from '../../../../auth/useUserContext';
import { useLiveVisitContext } from '../hooks/useLiveVisitContext';
// existing chat components
import ChatHeaderDetail from '../../chat/header/ChatHeaderDetail';
import ChatMessageInput from '../../chat/message/ChatMessageInput';
import ChatMessageList from '../../chat/message/ChatMessageList';
// types
import { IChatSendMessage } from '../../../../@types/chat';

// ----------------------------------------------------------------------

interface ChatMessage {
  sid: string;
  body: string;
  author: string;
  dateCreated: string | Date; // Twilio returns dateCreated as ISO string
  participantSid: string;
}

interface VisitChatRoomProps {
  conversationId: string;
  isOpen?: boolean;
  onClose?: () => void;
  variant?: 'full' | 'drawer';
}

export default function VisitChatRoom({
  conversationId,
  isOpen = false,
  onClose,
  variant = 'full',
}: VisitChatRoomProps) {
  const { t } = useTranslation();
  const { enqueueTranslated } = useTranslatedSnackbar();
  const { enqueueSnackbar } = useSnackbar();
  const { data: userData } = useUserContext();
  const navigate = useNavigate();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { mutateAsync: getChatMessagesAsync } = useMutation({
    mutationFn: (conversationId: string) =>
      conversationApi.conversationControllerGetChatMessages({ conversationId }),
  });

  const { mutateAsync: sendChatMessageAsync } = useMutation({
    mutationFn: (sendChatMessageRequest: SendChatMessageRequest) =>
      conversationApi.conversationControllerSendChatMessage({
        sendChatMessageRequest,
      }),
  });

  // Get visit participants from the new API endpoint
  const { data: visitData } = useLiveVisitContext();
  const { data: visitParticipants } = useQuery({
    queryKey: ['visitUsers', visitData?.id],
    queryFn: () =>
      visitApi.visitControllerFetchUserByVisitId({
        visitId: visitData?.id as string,
      }),
    enabled: Boolean(visitData?.id),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
  // Load messages from visit backend
  const loadMessages = useCallback(async () => {
    if (!conversationId) return;

    setIsLoading(true);
    try {
      const messagesResponse = await getChatMessagesAsync(conversationId);

      const transformedMessages: ChatMessage[] = (
        messagesResponse.data.messages || []
      ).map((msg) => {
        // Backend returns raw Twilio messages with dateCreated field
        const rawMessage = msg as any; // Type assertion for raw Twilio message
        const dateCreated = rawMessage.dateCreated
          ? new Date(rawMessage.dateCreated)
          : new Date();
        if (isNaN(dateCreated.getTime())) {
          console.warn(
            'Invalid dateCreated from backend:',
            rawMessage.dateCreated,
            'for message:',
            rawMessage.sid,
          );
          return {
            sid: rawMessage.sid,
            body: rawMessage.body,
            author: rawMessage.author,
            dateCreated: new Date(), // Fallback to current time
            participantSid: rawMessage.participantSid,
          };
        }
        return {
          sid: rawMessage.sid,
          body: rawMessage.body,
          author: rawMessage.author,
          dateCreated,
          participantSid: rawMessage.participantSid,
        };
      });

      setMessages(transformedMessages);
    } catch (error) {
      console.error('Error loading messages:', error);
      enqueueTranslated('snackbar.chatMessagesLoadError', {
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [conversationId, enqueueTranslated, getChatMessagesAsync]);

  useEffect(() => {
    const isTerminalState =
      visitData?.status === GetVisitByIdStatusEnum.Completed ||
      visitData?.status === GetVisitByIdStatusEnum.Cancelled ||
      visitData?.status === GetVisitByIdStatusEnum.Aborted;
    if (isTerminalState) {
      enqueueSnackbar(
        String(
          visitData?.status === GetVisitByIdStatusEnum.Completed
            ? t('visit.chatVisitEnded')
            : t('visit.chatVisitCancelled'),
        ),
        { variant: 'info' },
      );
      navigate(PATH_DASHBOARD.general.upcomingVisits);
    }
  }, [visitData?.status, enqueueSnackbar, navigate, t]);

  // Send message using visit backend
  const handleSendMessage = useCallback(
    async (data: IChatSendMessage) => {
      if (!conversationId) return;

      // Optimistic update - add message immediately for better UX
      const optimisticMessage = {
        sid: `temp-${Date.now()}`,
        body: data.message,
        author: userData?.id || '',
        dateCreated: new Date(),
        participantSid: userData?.id || '',
      };

      setMessages((prev) => [...prev, optimisticMessage]);

      try {
        // Send message using React Query mutation
        await sendChatMessageAsync({
          conversationId,
          message: data.message,
        });

        // Refresh messages after sending to get the real message
        await loadMessages();
      } catch (error) {
        console.error('Error sending message:', error);
        enqueueTranslated('snackbar.chatSendError', {
          variant: 'error',
        });

        // Remove optimistic message on error
        setMessages((prev) =>
          prev.filter((msg) => msg.sid !== optimisticMessage.sid),
        );
      }
    },
    [
      conversationId,
      loadMessages,
      enqueueTranslated,
      userData?.id,
      sendChatMessageAsync,
    ],
  );

  // Store loadMessages in ref to avoid recreating interval
  const loadMessagesRef = useRef(loadMessages);
  loadMessagesRef.current = loadMessages;

  // Load messages on mount and set up polling for new messages
  useEffect(() => {
    if (!conversationId) return;

    // Initial load
    loadMessagesRef.current();

    // Set up polling interval
    const interval = setInterval(() => {
      loadMessagesRef.current();
    }, 5000);

    return () => clearInterval(interval);
  }, [conversationId]); // Only depend on conversationId, not loadMessages

  // Transform backend messages to chat UI format
  const transformedMessages = messages.map((msg) => {
    // Parse dateCreated from backend (Twilio uses 'dateCreated', not 'timestamp')
    let createdAt: Date;

    try {
      // Backend returns 'dateCreated' field as ISO string
      if (typeof msg.dateCreated === 'string') {
        createdAt = new Date(msg.dateCreated);
      } else if (msg.dateCreated instanceof Date) {
        createdAt = msg.dateCreated;
      } else {
        throw new Error(
          `Missing or invalid dateCreated field: ${typeof msg.dateCreated}`,
        );
      }

      // Validate the parsed date
      if (isNaN(createdAt.getTime())) {
        throw new Error('Invalid date value');
      }
    } catch (error) {
      console.error('❌ Failed to parse dateCreated for message:', msg.sid, {
        dateCreated: msg.dateCreated,
        dateCreatedType: typeof msg.dateCreated,
        error: error.message,
      });
      createdAt = new Date(); // Fallback to current time only as last resort
    }

    return {
      id: msg.sid,
      body: msg.body,
      contentType: 'text' as const,
      attachments: [],
      createdAt,
      senderId: msg.author,
    };
  });

  const uniqueSenderIds = Array.from(
    new Set(messages.map((msg) => msg.author)),
  );
  const participants = uniqueSenderIds.map((senderId) => {
    if (senderId === userData?.id && userData) {
      // Current authenticated user
      return {
        id: userData.id,
        name: `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim(),
        username: userData?.email || '',
        avatar: userData?.avatar || '',
        address: '',
        phone: '',
        email: userData?.email || '',
        lastActivity: new Date(),
        status: 'online' as const,
        role: userData.roles?.includes('PROFESSIONAL')
          ? 'professional'
          : 'user',
      };
    } else {
      const visitUsers = visitParticipants?.data as any;
      let otherParticipant = null;
      let participantRole = 'user';

      if (visitUsers) {
        if (visitUsers.professional?.id === senderId) {
          otherParticipant = visitUsers.professional;
          participantRole = 'professional';
        } else if (visitUsers.user?.id === senderId) {
          otherParticipant = visitUsers.user;
          participantRole = 'user';
        }
      }

      return {
        id: senderId,
        name: otherParticipant
          ? `${otherParticipant.firstName || ''} ${otherParticipant.lastName || ''
            }`.trim()
          : 'Chat Participant',
        username: otherParticipant?.email || '',
        avatar: otherParticipant?.avatar || '',
        address: '',
        phone: '',
        email: otherParticipant?.email || '',
        lastActivity: new Date(),
        status: 'online' as const,
        role: participantRole,
      };
    }
  });

  const conversation = {
    id: conversationId,
    participants,
    messages: transformedMessages,
    unreadCount: 0,
    type: 'ONE_TO_ONE' as const,
  };

  const renderChatContent = () => (
    <Stack style={{ height: '100%' }}>
      <ChatHeaderDetail participants={participants} />

      <Stack
        direction="row"
        style={{
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Stack style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
          <ChatMessageList conversation={conversation} />

          <ChatMessageInput
            conversationId={conversationId}
            onSend={handleSendMessage}
            disabled={isLoading}
          />
        </Stack>
      </Stack>
    </Stack>
  );

  if (variant === 'drawer') {
    return (
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          style: {
            width: '320px',
            height: '100%',
          },
        }}
      >
        {renderChatContent()}
      </Drawer>
    );
  }

  return (
    <Box style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {renderChatContent()}
    </Box>
  );
}
